import EnvironmentIcon from '@mui/icons-material/SettingsInputComponent';
import Warning from '@mui/icons-material/Warning';
import { Box, Chip, Grid } from '@mui/material';
import Card from 'components/Card/Card.js';
import CardBody from 'components/Card/CardBody.js';
import CardHeader from 'components/Card/CardHeader.js';
import CardIcon from 'components/Card/CardIcon.js';
import Snackbar from 'components/Snackbar/Snackbar';
import { useEffect, useState } from 'react';

import RegularTab from 'components/Tabs';
import ThemeTab from 'components/Tenant/ThemeTab';
import LocalizationTab from 'components/Tenant/LocalizationTab';

import { makeStyles, withStyles } from '@mui/styles';
import {
  useGetClientTenantsQuery,
  useGetTenantClientQuery,
} from 'api/client.api';
import styles from 'assets/jss/material-dashboard-pro-react/views/tenantStyle.js';
import Button from 'components/CustomButtons/Button.js';
import CustomFieldsTab from 'components/Tenant/CustomFieldsTab';
import GeneralTab from 'components/Tenant/GeneralTab';
import IntegrationSettingsTab from 'components/Tenant/IntegrationSettingsTab';
import ProjectSettingsTab from 'components/Tenant/ProjectSettingsTab';
import { toast, ToastContainer } from 'react-toastify';
import {
  useActivateTenantMutation,
  useGetTenantQuery,
} from '../../api/tenant.api';
import { PromptAlert, RouterPrompt } from '../../features/ux/RouterPrompt';
import { tenantValidationForActivation } from '../../utils/tenantValidationForActivation';
import AllowedUrls from './AllowedUrls';

const useStyles = makeStyles(styles);

export default function Tenant() {
  const classes = useStyles();
  const { data: tenant } = useGetTenantQuery();
  const [activateTenant] = useActivateTenantMutation();

  const { data: currentClient } = useGetTenantClientQuery();
  const { data: clientTenants } = useGetClientTenantsQuery(currentClient?._id);

  const [tabValue, setTabValue] = useState(0);
  const [showDirtyClearAlert, setShowDirtyClearAlert] = useState(false);
  const [showSnackBar, setShowSnackBar] = useState(false);
  const [snackbarMessage, setSnackBarMessage] = useState('');

  const [tenantStatus, setTenantStatus] = useState('');
  const [baseTenantStatus, setBaseTenantStatus] = useState('');

  const [customTabStyle, setCustomTabStyle] = useState({});
  const [customCardStyle, setCustomCardStyle] = useState({});

  const [activeTabIsDirty, setActiveTabIsDirty] = useState(false);
  const [nextTabPending, setNextTabPending] = useState(0);

  const [showActivateEnvModal, setShowActivateEnvModal] = useState(false);
  const [activeEnableTenant, setActiveEnableTenant] = useState(false);

  const getCurrentTenantStatus = (tenant_id) => {
    if (!tenant_id) return null;
    let clientTenant = clientTenants?.find((ct) => ct.tenant_id === tenant_id);
    return clientTenant ? clientTenant.status : null;
  };

  useEffect(() => {
    if (tenant) {
      let baseTenantStatus = getCurrentTenantStatus(tenant.tenant_id);
      setBaseTenantStatus(baseTenantStatus);
      setTenantStatus(baseTenantStatus);

      if (baseTenantStatus === 'new') {
        setActiveEnableTenant(tenantValidationForActivation(tenant));
      }
    }
  }, [tenant, clientTenants]);

  const handleChangeTab = (event, newValue, validateDirty = true) => {
    setNextTabPending(newValue);

    if (activeTabIsDirty && validateDirty) {
      setShowDirtyClearAlert(true);
      return;
    }

    if (newValue === 3) {
      setCustomTabStyle({
        backgroundColor: 'transparent',
        padding: 0,
      });
      setCustomCardStyle({
        boxShadow: 'none',
      });
    } else {
      setCustomTabStyle({});
      setCustomCardStyle({});
    }
    setTabValue(newValue);
  };

  const [tabs, setTabs] = useState([
    { name: 'General', active: true, complete: false },
    { name: 'Localization', active: false, complete: false },
    { name: 'Project settings', active: false, complete: false },
    { name: 'Custom fields', active: false, complete: false },
    { name: 'Integration settings', active: false, complete: false },
    { name: 'Allowed URLs', active: false, complete: false },
    { name: 'Theme', active: false, complete: false },
  ]);

  useEffect(() => {
    // if (tenantStatus && tenantStatus !== 'new') {
    const clonedTabs = [...tabs];
    clonedTabs.forEach((tab) => {
      tab.active = true;
      tab.complete = true;
    });
    setTabs(clonedTabs);
    // }
  }, [tenantStatus]);

  const handleOnSuccessTab = (tabName) => {
    if (tenantStatus !== 'new') {
      return;
    }

    const updatedTabs = tabs.map((tab, index) => {
      if (tab.name === tabName) {
        tab.complete = true;
      }
      if (index > 0 && tabs[index - 1].complete) {
        tab.active = true;
      }
      return tab;
    });

    setActiveTabIsDirty(false);
    setTabs(updatedTabs);
  };

  const setDirtyTabStatus = (status) => {
    setActiveTabIsDirty(status);
  };

  const getTabContent = (tabIndex) => {
    const sharedProps = {
      onSuccessTab: handleOnSuccessTab,
      setDirtyTabStatus: setDirtyTabStatus,
      tenantStatus: tenantStatus,
    };

    switch (tabIndex) {
      case 0:
        return <GeneralTab {...sharedProps} tab={tabs[0]} />;
      case 1:
        return <LocalizationTab {...sharedProps} tab={tabs[1]} />;
      case 2:
        return <ProjectSettingsTab {...sharedProps} tab={tabs[2]} />;
      case 3:
        return (
          <CustomFieldsTab {...sharedProps} tab={tabs[3]} />
        );
      case 4:
        return (
          <IntegrationSettingsTab {...sharedProps} tab={tabs[4]} />
        );
      case 5:
        return (
          <AllowedUrls
            {...sharedProps}
            classes={classes}
            tab={tabs[5]}
            plain={true}
          />
        );
      case 6:
        return <ThemeTab {...sharedProps} tab={tabs[6]} />;
      default:
        return null;
    }
  };

  const StyledChip = withStyles({
    root: {
      backgroundColor: tenantStatus !== 'inactive' ? '#D7C7F7' : '#D95F4E',
      color: tenantStatus !== 'inactive' ? 'blueviolet' : 'white',
    },
  })(Chip);

  const handleCancelAlert = () => {
    setShowDirtyClearAlert(false);
    setNextTabPending(null);
  };

  const handleConfirmAlert = () => {
    setActiveTabIsDirty(false);
    setShowDirtyClearAlert(false);
    if (nextTabPending !== null) {
      handleChangeTab(null, nextTabPending, false);
    }
  };

  const activateEnv = async () => {
    if (!activeEnableTenant) {
      return;
    }
    try {
      await activateTenant({ tenant_id: tenant._id }).unwrap();
      setTenantStatus('active');

      toast.success('Tenant was activated', {
        position: toast.POSITION.BOTTOM_CENTER,
        autoClose: 2000,
        hideProgressBar: true,
      });
    } catch (error) {
      toast.error(
        'An error occurred while ' + 'trying to activate the tenant',
        {
          position: toast.POSITION.BOTTOM_CENTER,
          autoClose: 2000,
          hideProgressBar: true,
        }
      );
      console.error('Error activating tenant', error);
    }
  };

  return (
    <>
      <ToastContainer />
      <RouterPrompt
        when={activeTabIsDirty}
        title='Are you sure you want to leave?'
        message='You have unsaved changes that will be lost if you leave this page.'
        onOK={() => {
          setActiveTabIsDirty(false);
          return true;
        }}
        onCancel={() => false}
      />
      {showDirtyClearAlert && (
        <PromptAlert
          title='Discard Changes?'
          message='You have unsaved changes. Are you sure you want to discard them?'
          onConfirm={handleConfirmAlert}
          onCancel={handleCancelAlert}
        />
      )}
      {showActivateEnvModal && (
        <PromptAlert
          title='Confirm changes'
          message={`Are you sure you wish to activate the tenant?    Currency cannot be changed once tenant becomes Active.`}
          onConfirm={() => {
            setShowActivateEnvModal(false);
            activateEnv();
          }}
          onCancel={() => {
            setShowActivateEnvModal(false);
          }}
        />
      )}
      <Grid container>
        <Snackbar
          place='tr'
          color='rose'
          icon={Warning}
          message={snackbarMessage}
          open={showSnackBar}
          closeNotification={() => setShowSnackBar(false)}
          close
        />
        <Grid item xs={12}>
          <Card style={customCardStyle} className={classes.transparentCard}>
            <CardHeader color='rose' className={classes.cardHeader} icon>
              <div id='environment-info'>
                <CardIcon color='rose' className={classes.cardIcon}>
                  <EnvironmentIcon />
                </CardIcon>
                <h4 className={classes.cardIconTitle}>
                  Environment Information
                  <Chip
                    className={classes.chip}
                    label={tenant?.production ? 'Production' : 'Test'}
                    color={tenant?.production ? 'primary' : 'secondary'}
                  />
                  <StyledChip
                    className={classes.chip}
                    label={
                      tenantStatus?.charAt(0).toUpperCase() +
                      tenantStatus?.slice(1)
                    }
                    color={tenantStatus === 'active' ? 'primary' : 'secondary'}
                  />
                </h4>
              </div>
              {tenantStatus === 'new' && (
                <Button
                  id='activate-env'
                  variant='contained'
                  color='primary'
                  onClick={() => setShowActivateEnvModal(true)}
                  disabled={!activeEnableTenant}
                >
                  Activate Environment
                </Button>
              )}
            </CardHeader>

            <CardBody className={classes.cardBody}>
              <RegularTab
                style={{ backgroundColor: 'white' }}
                tabs={tabs}
                selectedTab={tabValue}
                onTabChange={handleChangeTab}
                color='primary'
              />
              <Box style={customTabStyle} className={classes.boxTabContent}>
                {getTabContent(tabValue)}
              </Box>
            </CardBody>
          </Card>
        </Grid>
      </Grid>
    </>
  );
}
